
/// <amd-module name="Release/Landing/HubContainer" />
import * as React from "react";

import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { css } from "VSSUI/Util";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";

import { IVssPageContext } from "VSS/Platform/Context";
import { VssComponent, Components, IVssComponentProperties, IVssComponentContext } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssHistoryService } from "VSS/Platform/History";
import { localeFormat } from "VSS/Core/Util/String";

import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";

import { IReleaseDefinitionPivotCommandContext } from "Release/Client/ExtensionContracts/IMenuCommandContracts";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";

import { MessageBarKeys } from "Constants";
import { SelectionStore } from "Release/Landing/Selection/SelectionStore";
import { ReleaseHub } from "Release/Landing/Hub";
import { IReleaseHubServiceDataService } from "Release/Landing/PageData";
import { TagsActionCreator } from "Release/Landing/HubContent/Filter/Tags/ActionCreator";
import { TagsStore } from "Release/Landing/HubContent/Filter/Tags/Store";
import { MessageActions } from "HubMessageBar/MessageActions";
import { MessageStore } from "HubMessageBar/MessageStore";
import { PipelinesStore } from "LeftPanel/PipelinesStore";
import { PipelineSearchStore } from "LeftPanel/PipelineSearch/PipelineSearchStore";
import { IPipelineCommandsService } from "Release/Commands/PipelineCommandsService";
import { IdentitiesActionCreator } from "./HubContent/Filter/Identities/ActionCreator";

import * as Resources from "Release/Landing/Resources";
import { IdentitesStore } from "./HubContent/Filter/Identities/Store";

interface IState {
    successMessage: React.ReactNode;
    errorMessage: React.ReactNode;
    signalRMessage: React.ReactNode;
    showZeroDay: boolean;
    loading: boolean;
}

class ReleaseHubContainer extends VssComponent<IVssComponentProperties, IState>{
    constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);

        // Dispose existing stores and initialize new stores 
        StoreManager.dispose();
        StoreManager.GetStore<SelectionStore>(SelectionStore);
        StoreManager.GetStore<PipelineSearchStore>(PipelineSearchStore);
        this._pipelinesStore = StoreManager.GetStore<PipelinesStore>(PipelinesStore, undefined, context.pageContext);
        this._pipelinesStore.addChangedListener(this._onPipelineStoreChange);

        const tagsActionCreator = ActionCreatorManager.GetActionCreator<TagsActionCreator>(TagsActionCreator);
        const identitesActionCreator = ActionCreatorManager.GetActionCreator<IdentitiesActionCreator>(IdentitiesActionCreator);
        StoreManager.GetStore<TagsStore>(TagsStore);
        StoreManager.GetStore<IdentitesStore>(IdentitesStore);
        tagsActionCreator.getProjectTags(this.context.pageContext);
        identitesActionCreator.getMostRecentIdentities(context.pageContext);

        this._messageActions = ActionsHubManager.GetActionsHub<MessageActions>(MessageActions);
        this._messageStore = StoreManager.GetStore<MessageStore>(MessageStore);
        this._messageStore.addChangedListener(this._onMessageStoreChange);

        const historyDefinitionId: string = context.pageContext.getService<IVssHistoryService>("IVssHistoryService").getState().definitionId as string;
        const rmDataService = this.context.pageContext.getService<IReleaseHubServiceDataService>("IReleaseHubServiceDataService");

        rmDataService.subscribe((data, updateEvent) => this.setState({
            showZeroDay: data.hasAnyDefinition === false, loading: !data.hasAnyDefinition && data.hasAnyDefinition !== false  /*checking for undefined*/
        }));

        const hasAnyPipelines = rmDataService.hasAnyReleaseDefinitions();
        this.state = {
            successMessage: "",
            errorMessage: hasAnyPipelines === false && !!historyDefinitionId ? localeFormat(Resources.PipelineIdNotFound, historyDefinitionId) : "",
            signalRMessage: "",
            showZeroDay: hasAnyPipelines === false,
            loading: !hasAnyPipelines && hasAnyPipelines !== false // hasPipelines is undefined
        };
    }

    public componentWillUnmount(): void {
        this._pipelinesStore.removeChangedListener(this._onPipelineStoreChange);
        this._messageStore.removeChangedListener(this._onMessageStoreChange);
    }

    public render(): React.ReactNode {
        let content: React.ReactNode = null;

        if (this.state.loading) {
            content = (<Spinner className={"flex flex-grow"} size={SpinnerSize.large} />);
        }
        else if (!this.state.showZeroDay) {
            content = (<ReleaseHub />);
        }
        else {
            content = this._zeroDay();
        }

        return (
            <div className={"flex flex-column flex-grow"} >
                {
                    this.state.successMessage &&
                    <MessageBar
                        className={"releases-hub-success-message"}
                        onDismiss={() => this._messageActions.setMessage.invoke({ key: MessageBarKeys.HubSuccess, value: "" })}
                        severity={MessageBarSeverity.Success}>
                        {this.state.successMessage}
                    </MessageBar>
                }
                {
                    this.state.errorMessage &&
                    <MessageBar
                        className={"releases-hub-error-message"}
                        onDismiss={() => this._messageActions.setMessage.invoke({ key: MessageBarKeys.HubError, value: "" })}
                        severity={MessageBarSeverity.Error}>
                        {this.state.errorMessage}
                    </MessageBar>
                }
                {
                    this.state.signalRMessage &&
                    <MessageBar
                        className={"releases-hub-success-message" /* Using the same class as success as there's no real difference */}
                        onDismiss={() => this._messageActions.setMessage.invoke({ key: MessageBarKeys.SignalR, value: "" })}
                        severity={MessageBarSeverity.Info}>
                        {this.state.signalRMessage}
                    </MessageBar>
                }

                {content}

            </div >
        );
    }

    private _zeroDay(): React.ReactNode {

        const secondaryText = (
            <div className="zero-day-secondary-text">
                {Resources.NewHubZeroDaySecondaryText}
            </div>
        );

        // In IE, zoom on img zooms the image, but doesn't take care of realigning. Therefore for IE, we would like to keep zoom as 1.
        // For firefox, img zoom doesn't work, unless addons are installed. -moz-transform with scale(2) works, but doesn't realign other items
        const userAgent = navigator.userAgent.toLowerCase();
        const isIE = /(msie [\d.]+|trident\/[\d.]+)/.test(userAgent);

        return (
            <div className={css("release-zero-day-container absolute-fill justify-center", isIE ? "browser-ie" : "")}>
                <ZeroData
                    imagePath={getAssetLocation(this.context.pageContext, "ms.vss-releaseManagement-web/release-landing/zerodata-release-management-new.png")}
                    imageAltText={""}
                    primaryText={Resources.NewHubZeroDayPrimaryText}
                    secondaryText={secondaryText}
                    actionText={Resources.NewHubZeroDayCreateDefinitionButtonText}
                    actionType={ZeroDataActionType.ctaButton}
                    onActionClick={this._navigateToCreateReleaseDefinition}
                />
            </div>);
    }

    private _navigateToCreateReleaseDefinition = (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void => {
        const commandContext = {
            pageContext: this.context.pageContext
        } as IReleaseDefinitionPivotCommandContext;

        this.context.pageContext.getService<IPipelineCommandsService>("IPipelineCommandsService").createNewPipeline(commandContext, event as React.MouseEvent<HTMLElement>);
    }

    private _onMessageStoreChange = (): void => {
        const state = this._messageStore.getState();
        this.setState({
            successMessage: state[MessageBarKeys.HubSuccess],
            errorMessage: state[MessageBarKeys.HubError],
            signalRMessage: state[MessageBarKeys.SignalR]
        });
    }

    private _onPipelineStoreChange = (): void => {
        const isActiveEmpty = this._pipelinesStore.isActiveListEmptied();
        this.setState({ showZeroDay: isActiveEmpty });
    }

    private _pipelinesStore: PipelinesStore;
    private _messageStore: MessageStore;
    private _messageActions: MessageActions;
}

Components.add("release-landing-hub", ReleaseHubContainer);
